/**
 * Gift recipients — the pure half: copy, and turning typed drafts into the
 * request shape. No React, so both checkout steps and their tests share it.
 *
 * @module pages/subscription/steps/giftRecipients
 */

import { canHold, heldFromPayload } from '@butter/library/subscription';
import type { HoldingsPayload } from '@lgc/database/types/domains/subscription';
import type { ApiGiftRecipient } from '@lgc/database/types/domains/pass';
import type { GiftRecipientDraft } from '~/services/internalApi/subscription';
import { createCompositeId } from '~/lib/utils/subscriptionIdUtils';

/**
 * ONE line for every reason a person cannot receive a gift. The
 * buyer is naming a third party, so the copy must not reveal which is true.
 */
export const GIFT_RECIPIENT_INELIGIBLE_COPY =
  'This person already has a pass or membership and cannot receive this gift.';

/**
 * Shown until a promo supplies its own Gifting Message. One active
 * pass per member is a system rule; this is its plain-language form.
 */
export const DEFAULT_GIFTING_MESSAGE =
  'Each additional pass must be gifted to someone else. Enter the recipient\'s details below.';

/**
 * The gift-all form of the above: with the buyer keeping none, "each ADDITIONAL
 * pass" is simply false — there is no first pass of theirs for the others to be
 * additional to.
 */
export const GIFT_ALL_MESSAGE =
  'Every pass in this order goes to someone else. Enter their details below.';

/**
 * The note an applied gifting promo shows under the promo field, even at
 * quantity 1 — null when the code does not require gifting.
 */
export function promoGiftingNote(
  promotion: { applied: boolean; requiresGifting?: boolean; giftingMessage?: string } | undefined,
): string | null {
  if (!promotion?.applied || !promotion.requiresGifting) return null;
  return promotion.giftingMessage || DEFAULT_GIFTING_MESSAGE;
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** "Bob Smith" → first + last. The design draws ONE name field; the API wants two. */
function splitName(name: string): { firstName: string; lastName: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return { firstName: parts[0] ?? '', lastName: parts.slice(1).join(' ') };
}

/** A draft is complete when it has a first AND last name and a plausible address. */
export function isRecipientComplete(draft: GiftRecipientDraft | undefined): boolean {
  if (!draft) return false;
  const { firstName, lastName } = splitName(draft.name);
  return firstName !== '' && lastName !== '' && EMAIL_SHAPE.test(draft.email.trim());
}

/**
 * Resolve the gift-all checkbox from the buyer's answer and what they hold.
 *
 * Pure, and here rather than in the step, because it governs what goes on the
 * wire — exactly the kind of thing that rots inside a thousand-line component.
 *
 * | may hold a pass? (`canHold`) | stored | effective | locked |
 * |------------------------------|--------|-----------|--------|
 * | no — holds a pass or a membership, live, queued or scheduled | any | true | yes |
 * | yes                          | null   | false     | no     |
 * | yes                          | true   | true      | no     |
 *
 * The lock is the server's own rule: the purchase saga runs the holding matrix
 * (`assertCanHoldPass`) for a buyer who keeps a pass, and refuses beside ANY
 * membership — the team's, a personal one, a queued one — or a pass, active or
 * scheduled (subscription unification D10/D11.5). Unticking would only buy
 * that refusal at Pay, so the box locks for exactly the holders the server
 * refuses, read off the same `holdings` cards through the same `canHold`.
 * `lockedBy` names the kind so the note can say which; `lockedByName` is that
 * card's plan name when the wire carried one.
 *
 * `stored === null` is "not answered": with a plain boolean the box could not
 * tell an untick from a default. Nothing defaults any more, so a holder who may
 * buy starts unticked.
 */
export function resolveGiftAll(input: {
  stored: boolean | null;
  holdings: HoldingsPayload | null;
}): { effective: boolean; locked: boolean; lockedBy: 'pass' | 'membership' | null; lockedByName: string | null } {
  const held = input.holdings ? heldFromPayload(input.holdings) : [];
  const verdict = canHold('pass', held);
  if (verdict.allowed) {
    return { effective: input.stored ?? false, locked: false, lockedBy: null, lockedByName: null };
  }
  const offender = verdict.held[0];
  const lockedBy = verdict.reason === 'held_pass' ? 'pass' : 'membership';
  const card = lockedBy === 'pass'
    ? input.holdings?.passes.find((c) => c.id === offender?.id)
    : input.holdings?.memberships.find((c) => c.id === offender?.id);
  return { effective: true, locked: true, lockedBy, lockedByName: card?.plan?.name ?? null };
}

/**
 * How many recipients an order of `quantity` needs.
 *
 * The buyer is holder #1 unless they said otherwise: ticking "this is a gift"
 * gives every pass away, including the first. That one subtraction
 * is the whole difference between the two shapes, and the API accepts both —
 * `resolveGiftHolders` takes `quantity − 1` or `quantity`, nothing between.
 */
export function recipientsNeeded(quantity: number, giftAll: boolean): number {
  return giftAll ? quantity : quantity - 1;
}

/**
 * Every recipient the order needs is complete. Vacuously true for a single pass
 * the buyer is keeping — but NOT for a single pass they are gifting.
 */
export function areRecipientsComplete(
  recipients: GiftRecipientDraft[],
  quantity: number,
  giftAll = false,
): boolean {
  for (let i = 0; i < recipientsNeeded(quantity, giftAll); i += 1) {
    if (!isRecipientComplete(recipients[i])) return false;
  }
  return true;
}

/**
 * The order's gifting fields for the create request — EMPTY for a single pass,
 * so that request is byte-identical to the one checkout has always sent.
 */
export function giftOrderFields(
  recipients: GiftRecipientDraft[],
  quantity: number,
  giftAll = false,
): { quantity?: number; recipients?: ApiGiftRecipient[] } {
  const needed = recipientsNeeded(quantity, giftAll);
  // Nothing to say: one pass, kept. NOT reachable via `quantity <= 1` any more —
  // a single GIFTED pass is quantity 1 with one recipient, and returning `{}`
  // there would silently turn the buyer's gift into a purchase for themselves.
  if (needed <= 0) return {};
  return {
    quantity,
    recipients: recipients.slice(0, needed).map((draft) => ({
      ...splitName(draft.name),
      email: draft.email.trim().toLowerCase(),
    })),
  };
}

/**
 * Where a completed pass purchase's receipt lives.
 *
 * A PURE GIFT returns no `userPass` — the buyer kept nothing, so there is no row
 * of theirs to key a receipt on, and the order's anchor belongs to a recipient
 * whose pass they may not read. Its receipt is keyed by the INVOICE they paid,
 * which is the thing they do own.
 *
 * Shared by both checkout steps so the two cannot drift; `null` only if the
 * server returned neither, which a paid purchase never does.
 */
export function passReceiptCompositeId(payload: {
  userPass?: { id: number };
  invoice?: { id: number } | null;
}): string | null {
  if (payload.userPass) return createCompositeId('pass', payload.userPass.id);
  if (payload.invoice) return createCompositeId('gift', payload.invoice.id);
  return null;
}

/** `recipients.1.email` → 1. The API names the refused recipient in `param`. */
export function refusedRecipientIndex(param: string | undefined | null): number | null {
  const match = /^recipients\.(\d+)\./.exec(param ?? '');
  return match ? Number(match[1]) : null;
}

/**
 * How to NAME a refused recipient on a step that cannot point at their field
 * (the guest path pays from Finish Account, where the recipient block is not
 * rendered). Only ever the buyer's own typed input — the refusal itself stays
 * vague.
 */
export function refusedRecipientLabel(recipients: readonly GiftRecipientDraft[], index: number | null): string | null {
  if (index === null) return null;
  const recipient = recipients[index];
  return recipient?.name.trim() || recipient?.email.trim() || null;
}
